/* global cookieUtils  */
import React, {
  createRef,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useReducer,
  useState,
} from 'react';
import classNames from 'classnames';
import { bool, func, shape, string } from 'prop-types';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import THDCustomer from '@thd-olt-global/thd-customer';
import { getDomPath, ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { useProductDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';
import { useDataModel } from '@thd-nucleus/data-sources';
import { Tooltip } from '@thd-olt-component-react/tooltip';
import { Input } from '@thd-olt-component-react/input';
import { Image } from '@thd-olt-component-react/image';
import { Col } from '@thd-olt-component-react/grid';
import { Button } from '@thd-olt-component-react/button';
import { trackAddCollection, trackAddItem, trackClick, trackCreateAList, trackOverlay } from './analytics';
import './favorite.style.scss';
import { dataModel } from './dataModel';

let customerInfoCache;

const initialState = {
  favoriteCount: 0,
  isFavorite: false,
  disableTooltip: false,
  successSave: {
    status: false,
    listId: '',
    listName: ''
  },
  shouldCreateList: false,
  shouldOpenTooltip: false,
  customerInfo: {},
  itemPrice: null,

};

function stateReducer(state, { action, value }) {
  switch (action) {
  case 'increaseFavoriteCount':
    return { ...state, favoriteCount: (state?.favoriteCount + value) };
  case 'setFavoriteCount': return state?.favoriteCount === value
    ? state
    : { ...state, favoriteCount: value };
  case 'isFavorite': return state.isFavorite === value
    ? state
    : { ...state, isFavorite: value };
  case 'disableTooltip': return state.disableTooltip === value
    ? state
    : { ...state, disableTooltip: value };
  case 'successSave':
    return { ...state, successSave: value };
  case 'shouldCreateList': return state.shouldCreateList === value
    ? state
    : { ...state, shouldCreateList: value };
  case 'shouldOpenTooltip': return state.shouldOpenTooltip === value
    ? state
    : { ...state, shouldOpenTooltip: value };
  case 'customerInfo':
    return { ...state, customerInfo: value };
  case 'itemPrice':
    return { ...state, itemPrice: value };
  default:
    return state;
  }
}

const Favorite = (props) => {
  const {
    className,
    collectionId,
    collectionImage,
    iconClass,
    itemId,
    label,
    onFavoriteButtonClick,
    pageType,
    showCircle,
    showCount,
    small,
    storeId: propStoreId,
    vertical
  } = props;
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const { channel, isServer } = useContext(ExperienceContext);
  const defaultStoreId = useStoreId();
  const storeId = propStoreId || defaultStoreId;
  const theme = useTheme(Favorite, props);
  const { hideCount, showOverlay, showPlusSign } = theme.props;
  const favoritesRef = createRef();
  const listNameRef = useRef(null);
  const { customerAccountID, userId, customerType } = state?.customerInfo || {};

  useEffect(() => {
    async function getAGUI() {
      const agui = await Favorite.getAccountGlobalUI();
      Favorite.accountglobaluilist = agui;
      if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('save-to-favorites.ready');
      }
    }
    getAGUI();
    // THDCustomer getters parse all the cookies in the document, causing a
    // significant performance hit for doing this on every render of Favorite.
    // This caches the data we need from it to fix performance.
    // @TODO: Put the below information into the customer-information hook
    // currently work-in-progress from B2B team.
    customerInfoCache = customerInfoCache || {
      customerAccountID: window.THDCustomer?.default?.svocID,
      userId: window.THDCustomer?.default?.userID,
      customerType: window.THDCustomer?.default?.customerType || 'B2C'
    };
    dispatch({ action: 'customerInfo', value: customerInfoCache });
  }, []);

  const opts = {
    ssr: false,
    skip: (!customerAccountID || !userId),
    fetchPolicy: 'cache-first',
    variables: {
      customerAccountID,
      userId,
      customerType
    }
  };
  const {
    data: myListData
  } = useDataModel('listDetails', opts);

  const createList = useCallback(async (listName) => {
    if (listName !== '') {
      let response;
      if (collectionId) {
        response = await THDCustomer.createListWithCollection(
          listName,
          customerAccountID,
          collectionId
        );
      } else {
        response = await THDCustomer.createListWithItem(
          listName,
          customerAccountID,
          itemId
        );
      }
      response = JSON.parse(response);
      if (response?.ListResponse?.status === 'SUCCESS' || response?.ListResponseV2?.status === 'SUCCESS') {
        const listId = response?.ListResponse?.listId || response?.ListResponseV2?.listId;
        if (showCount && !hideCount) {
          dispatch({ action: 'increaseFavoriteCount', value: 1 });
        }
        dispatch({ action: 'isFavorite', value: true });
        dispatch({ action: 'successSave',
          value: {
            status: true,
            listId,
            listName
          } });
        setTimeout(() => {
          // This will auto-close the tooltip after 3 seconds
          dispatch({ action: 'successSave',
            value: {
              status: false
            } });
        }, 5000);
        dispatch({ action: 'shouldOpenTooltip', value: false });
        dispatch({ action: 'shouldCreateList', value: false });
        if (collectionId) {
          trackAddCollection(collectionId);
        } else {
          trackAddItem(1, state?.itemPrice || 0, itemId);
        }
      }
    }
  }, [collectionId, customerAccountID, itemId, state?.itemPrice, hideCount, showCount]);

  const saveItemToList = useCallback(async (list, event) => {
    const { onClick, onFavoriteChange } = props;
    let response;
    if (collectionId) {
      response = await THDCustomer.saveCollectionToList(
        list.listName,
        list.listId,
        customerAccountID,
        collectionId
      );
    } else {
      response = await THDCustomer.saveItemToList(
        list.listName,
        list.listId,
        customerAccountID,
        itemId
      );
    }
    response = JSON.parse(response);
    if (response?.ListResponse?.status === 'SUCCESS' || response?.ListResponseV2?.status === 'SUCCESS') {
      if (showCount && !hideCount) {
        dispatch({ action: 'increaseFavoriteCount', value: 1 });
      }
      dispatch({ action: 'isFavorite', value: true });
      dispatch({ action: 'successSave',
        value: {
          status: true,
          listId: list.listId,
          listName: list.listName
        } });
      setTimeout(() => {
        // This will auto-close the tooltip after 3 seconds
        dispatch({ action: 'successSave',
          value: {
            status: false
          } });
      }, 5000);
      if (collectionId) {
        trackAddCollection(collectionId);
      } else {
        trackAddItem(1, state?.itemPrice || 0, itemId);
      }
      if (onFavoriteChange) {
        onFavoriteChange(parseInt(itemId, 0));
      }
      if (onClick) {
        onClick(event, { ...props });
      }
      if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
        window.LIFE_CYCLE_EVENT_BUS.trigger('save-to-favorites.click', { ...props });
      }
    }
  }, [customerAccountID, collectionId, itemId, props, hideCount, state?.itemPrice, showCount]);

  useEffect(() => {
    const handleKeypress = (event) => {
      if (event.code === 'Enter' && event.target.classList.contains('tooltip-content__input')) {
        createList(listNameRef?.current?.state?.value);
      }
    };
    document.addEventListener('keypress', handleKeypress, false);
    return () => {
      document.removeEventListener('keypress', handleKeypress, false);
    };
  }, [createList]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.getURLParameters) {
      const params = window.getURLParameters() || {};
      const autoFavCollection = params.autofavcollection && params.autofavcollection === collectionId;
      const autoFavItem = params.autofavitem && params.autofavitem === itemId;
      if ((autoFavCollection || autoFavItem) && myListData) {
        const { listDetails = [] } = myListData?.listDetails?.listResponse;
        if (listDetails.length === 1) {
          let savedAlready = false;
          listDetails[0].items.forEach((item) => {
            if (item.itemId === itemId || item.itemId === collectionId) {
              savedAlready = true;
            }
          });
          if (!savedAlready) {
            saveItemToList(listDetails[0]);
          }
        } else {
          dispatch({ action: 'shouldOpenTooltip', value: true });
        }
      }
    }
  }, [myListData, collectionId, itemId, saveItemToList]);

  useEffect(() => {
    dispatch({ action: 'isFavorite', value: false });
    dispatch({ action: 'setFavoriteCount', value: 0 });
  }, [itemId]);

  if (typeof window !== 'undefined' && window.THDCustomer && myListData && !state?.isFavorite) {
    const { listDetails } = myListData?.listDetails?.listResponse || {};
    (listDetails || []).forEach((list) => {
      const { items } = list;
      (items || []).forEach((item) => {
        if (item.itemId === itemId) {
          dispatch({ action: 'isFavorite', value: true });
          if (showCount && !hideCount) {
            dispatch({ action: 'increaseFavoriteCount', value: 1 });
          }
        }
      });
    });
  }

  let iconClassList = classNames(
    iconClass,
    'Favorite-icon',
    {
      'Favorite-icon--selected': state?.isFavorite && !showPlusSign,
      'Favorite-icon--plus--selected': state?.isFavorite && showPlusSign,
      'Favorite-icon--display': !label,
      'Favorite-icon--plus': showPlusSign
    }
  );
  const [iconClasses, setIconClasses] = useState(iconClassList);

  const productDomains = ['media', 'pricing'];
  if (showCount) {
    productDomains.push('favoriteDetail');
  }

  const { data, loading, error } = useProductDeprecated({
    itemId,
    storeId,
    domains: productDomains,
    ssr: false,
    skip: !itemId
  });

  if ((isServer !== loading) || (error && !data)) {
    return null;
  }

  const favoriteCount = data?.product?.favoriteDetail?.count;
  if (showCount && !hideCount && favoriteCount && state?.favoriteCount < favoriteCount) {
    dispatch({ action: 'increaseFavoriteCount', value: favoriteCount });
  }

  const { image } = data?.product?.media || {};
  const imageUrl = image?.url.replace('<SIZE>', '400') || collectionImage;
  if (!state?.itemPrice && data?.product?.pricing?.value) {
    dispatch({ action: 'itemPrice', value: data?.product?.pricing?.value });
  }

  const saveToList = (event) => {
    const {
      onClick,
      onFavoriteChange,
    } = props;

    const path = getDomPath(favoritesRef);
    let analyticsSource = '';
    if (pageType === 'browse') {
      analyticsSource = 'plp';
    } else if (pageType === 'search') {
      analyticsSource = 'search results';
    } else if (path.indexOf('ProductPage[0]') > -1) {
      analyticsSource = 'pip';
    } else if (path.indexOf('PepPage[0]') > -1) {
      analyticsSource = 'pep';
    }

    const isMobileChannel = channel === 'mobile';
    const config = {
      source: analyticsSource,
      isMobileChannel,
      $trigger: event.currentTarget,
      item: {
        itemId: itemId || collectionId,
        type: collectionId ? 'collection' : 'item',
        quantity: 1,
        imageURL: imageUrl,
        price: state?.itemPrice
      }
    };

    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.on('ITEM_ADDED_TO_LIST', (payload) => {
        const { output = {} } = payload;
        const { item = {} } = output;
        if (item.itemId === itemId) {
          iconClassList = classNames(
            iconClass,
            'Favorite-icon',
            {
              'Favorite-icon--selected': !showPlusSign,
              'Favorite-icon--plus--selected': showPlusSign,
              'Favorite-icon--display': !label,
              'Favorite-icon--plus': showPlusSign
            }
          );
          setIconClasses(iconClassList);
          if (onFavoriteChange) {
            onFavoriteChange(parseInt(item.itemId, 0));
          }
        }
        window.LIFE_CYCLE_EVENT_BUS.off('ITEM_ADDED_TO_LIST');
      });
    }
    if (Favorite.accountglobaluilist && Favorite.accountglobaluilist.myList) {
      Favorite.accountglobaluilist.myList.saveToList(config);
    }
    if (onClick) {
      onClick(event, { ...props });
    }
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('save-to-favorites.click', { ...props });
    }
  };

  let favoriteCountRoundOff = state?.favoriteCount;
  const maxFavoriteCount = 9999;
  if (state?.favoriteCount && state?.favoriteCount > maxFavoriteCount) {
    favoriteCountRoundOff = `${(state?.favoriteCount / 1000).toFixed(1)}k`;
  }
  const circleClasses = classNames('Favorite-wrapper-icon', {
    'Favorite-wrapper--circle': showCircle,
    'Favorite-wrapper--circle-count': state?.favoriteCount > 0
  });

  const buttonClasses = classNames(
    className,
    'Favorite-wrapper',
    {
      'Favorite-wrapper__label--horizontal': label && !vertical,
      'Favorite-icon__label--vertical': label && vertical,
      'Favorite-wrapper--plus': showPlusSign,
      'Favorite-wrapper--small': small
    }
  );

  const labelClasses = classNames(
    'Favorite-wrapper__label',
    {
      'Favorite-wrapper__label--no-left-margin': showPlusSign && !showCircle
    }
  );

  let tooltipInfo = 'Save to favorites';
  if (customerAccountID) {
    tooltipInfo += ' or a new list';
  }
  const cancelCreateList = () => {
    dispatch({ action: 'shouldCreateList', value: false });
    dispatch({ action: 'shouldOpenTooltip', value: true });
  };
  const newListClick = () => {
    dispatch({ action: 'shouldCreateList', value: true });
    dispatch({ action: 'shouldOpenTooltip', value: true });
    trackCreateAList();
  };
  const signInRedirect = (location) => {
    const autoFav = collectionId ? `autoFavCollection=${collectionId}` : `autoFavItem=${itemId}`;
    if (typeof window !== 'undefined' && window.location) {
      window.location.href = `/auth/view/${location}?redirect=${window.location.pathname}?${autoFav}`;
    }
  };
  const ClickTooltipContent = forwardRef((contentProp, ref) => {
    if (myListData && !state?.successSave.status) {
      const { listDetails } = myListData?.listDetails?.listResponse || {};
      return (
        <>
          <ul className="tooltip-content">
            <li className="tooltip-content__add-to">Save to:</li>
          </ul>
          <ul ref={ref} className="tooltip-content tooltip-content--scrollable">
            {
              (listDetails || []).map((list) => {
                return (
                  <button
                    className="tooltip-content__button"
                    type="button"
                    onClick={(event) => saveItemToList(list, event)}
                  >
                    <li className="tooltip-content__list">
                      {list.listName}
                    </li>
                  </button>
                );
              })
            }
          </ul>
          <ul className="tooltip-content">
            {
              state?.shouldCreateList ? (
                <li className="tooltip-content__create-list">
                  <Input
                    autoFocus
                    className="tooltip-content__input"
                    data-testid="new-list-input"
                    errorText="You already have a list with that name. Please enter a different name."
                    header="List Name"
                    maxLength="50"
                    ref={listNameRef}
                    placeholder="New Project"
                    validation={(text) => {
                      let isDuplicate = false;
                      listDetails.forEach((list) => {
                        if (list.listName === text) {
                          isDuplicate = true;
                        }
                      });
                      return !isDuplicate;
                    }}
                  />
                  <Col fallback="4" className="cancel-button">
                    <Button
                      link
                      onClick={cancelCreateList}
                    >
                      Cancel
                    </Button>
                  </Col>
                  <Col fallback="8" className="create-button">
                    <Button
                      outline
                      disabled={listNameRef?.current?.state?.value === ''}
                      onClick={() => createList(listNameRef?.current?.state?.value)}
                      data-testid="create-button"
                    >
                      Create
                    </Button>
                  </Col>
                </li>
              ) : (
                <button
                  className="tooltip-content__button"
                  type="button"
                  onClick={newListClick}
                  data-testid="newlist-button"
                >
                  <li className="tooltip-content__new-list">
                    <Image
                      className="plus-skinny"
                      src="https://assets.thdstatic.com/images/v1/plus-skinny.svg"
                      alt="plus"
                      width="18"
                      height="18"
                    />New List
                  </li>
                </button>
              )
            }
          </ul>
        </>
      );
    }
    if (state?.successSave?.status) {
      return (
        <>
          <Image
            className="green-check"
            src="https://assets.thdstatic.com/images/v1/alert-check-green.svg"
            alt="green-check"
            width="23"
            height="23"
          />
          Saved to&nbsp;
          <a className="list-name" href={`/list/view/details/${state?.successSave?.listId}`}>
            {state?.successSave?.listName}
          </a>
        </>
      );
    }
    return (
      <div className="sign-in">
        Sign in to save to <br />
        favorites or a new list.
        <Button outline className="sign-in__button" onClick={() => signInRedirect('signin')}>
          Sign in
        </Button>
        <Button
          className="sign-in__create-account-button"
          onClick={() => signInRedirect('createaccount')}
        >
          Create an Account
        </Button>
      </div>
    );
  });

  const tooltipOnClick = () => {
    dispatch({ action: 'disableTooltip', value: !state?.disableTooltip });
    dispatch({ action: 'successSave', value: { status: false } });
    dispatch({ action: 'shouldOpenTooltip', value: false });
    if (!collectionId && window.digitalData?.page?.pageInfo?.pageType === 'pip') {
      trackClick();
    }
    trackOverlay();
    onFavoriteButtonClick();
  };

  const FavoriteButton = forwardRef((favProps, ref) => {
    return (
      <div ref={ref}>
        <Tooltip
          channel="desktop"
          closeButton
          content={<ClickTooltipContent />}
          interactive
          placement="bottom"
          showClose={false}
          visible={state?.disableTooltip
            || state?.successSave?.status
            || state?.shouldCreateList
            || state?.shouldOpenTooltip}
        >
          <button
            type="button"
            className={buttonClasses}
            onClick={tooltipOnClick}
            onClose={() => dispatch({ action: 'disableTooltip', value: false })}
            ref={favoritesRef}
            data-testid="favorites"
          >
            <span className={circleClasses}>
              <span className={iconClassList} />
              {state?.favoriteCount > 0 && (<span className="Favorite-wrapper--count">{favoriteCountRoundOff}</span>)}
            </span>
            {label && (<span className={labelClasses}>{label}</span>)}
          </button>
        </Tooltip>
      </div>
    );
  });

  return (
    <>
      {(itemId || collectionId) && (!showOverlay ? (
        <Tooltip
          channel="desktop"
          className="saveToListPromptText"
          content={tooltipInfo}
          disableTooltip={state?.disableTooltip}
          interactive
          maxWidth={125}
          placement="bottom"
        >
          <FavoriteButton />
        </Tooltip>
      ) : (
        <button
          type="button"
          className={buttonClasses}
          onClick={saveToList}
          ref={favoritesRef}
        >
          <span className={circleClasses}>
            <span className={iconClasses} />
            {state?.favoriteCount > 0 && (<span className="Favorite-wrapper--count">{favoriteCountRoundOff}</span>)}
          </span>
          {label && (<span className={labelClasses}>{label}</span>)}
        </button>
      ))}
    </>
  );
};

Favorite.getAccountGlobalUI = async () => {
  const { default: agui } = await import(/* webpackChunkName: "account-global-ui" */ 'account-global-ui');
  return agui;
};

Favorite.propTypes = {
  className: string,
  collectionId: string,
  collectionImage: string,
  iconClass: string,
  itemId: string,
  label: string,
  onClick: func,
  onFavoriteButtonClick: func,
  onFavoriteChange: func,
  pageType: string,
  showCircle: bool,
  showCount: bool,
  small: bool,
  storeId: string,
  vertical: bool,
};

Favorite.defaultProps = {
  className: null,
  collectionId: null,
  collectionImage: null,
  iconClass: null,
  itemId: null,
  label: null,
  onClick: null,
  onFavoriteButtonClick: () => {},
  onFavoriteChange: null,
  pageType: null,
  showCircle: false,
  showCount: false,
  small: false,
  storeId: null,
  vertical: false,
};

Favorite.themeProps = {
  hideCount: bool,
  showOverlay: bool,
  showPlusSign: bool
};

Favorite.defaultThemeProps = {
  hideCount: false,
  showOverlay: false,
  showPlusSign: false
};

Favorite.displayName = 'Favorite';

Favorite.dataModel = dataModel;

export { Favorite };
